import React, {memo} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import findIndex from 'lodash/findIndex';
import {Modal, NavLink} from 'reactstrap';
import styled from 'styled-components';

import {gt} from 'app/utils/i18n';
import {shallowEqual} from 'app/utils/react-utils';

export interface TabType {
  eventKey: string;
  name: string;
  icon: string;
  content: React.ReactNode;
}

interface Props
  extends Pick<React.ComponentProps<typeof Modal>, 'zIndex' | 'className'> {
  initiallySelectedTab?: string;
  show: boolean;
  onCancel: () => void;
  tabs: Array<TabType>;
  onClosed?: () => any;
  modalBodyClass?: string;
  scrollable?: boolean;
}

function TabbedModal({
  initiallySelectedTab,
  show,
  onCancel,
  tabs,
  onClosed,
  scrollable,
  modalBodyClass = '',
  zIndex,
  className,
}: Props): JSX.Element {
  const [activeTab, setActiveTab] = React.useState(() => {
    const selected =
      initiallySelectedTab != null
        ? findIndex(tabs, {eventKey: initiallySelectedTab})
        : 0;
    return Math.max(0, selected);
  });

  function handleSelect(ev: React.SyntheticEvent<HTMLElement>) {
    const eventKey = ev.currentTarget.id;
    const newActiveTab = findIndex(tabs, {eventKey});

    if (newActiveTab === -1) {
      return;
    }

    setActiveTab(newActiveTab);
  }

  return (
    <Modal
      isOpen={show}
      backdrop="static"
      toggle={onCancel}
      onClosed={onClosed}
      scrollable={scrollable}
      zIndex={zIndex}
      className={className}
    >
      <StyledModalHeaderDIV>
        <ul className="nav nav-tabs">
          {tabs.map((tab: TabType, index: number) => (
            <li className="nav-item" key={tab.eventKey}>
              <NavLink
                active={index === activeTab}
                onClick={handleSelect}
                id={tab.eventKey}
              >
                <i className={tab.icon} /> {tab.name}
              </NavLink>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onCancel}
          className="close"
          aria-label={gt('Close')}
        >
          <i className="fa fa-times" aria-hidden="true" />
        </button>
      </StyledModalHeaderDIV>
      <div className={'modal-body ' + modalBodyClass}>
        {tabs[activeTab].content}
      </div>
    </Modal>
  );
}

export default memo<Props>(TabbedModal, shallowEqual);

export const StyledModalFooter = styled.div.attrs<{className?: string}>({
  className: 'modal-footer no-gutters flex-wrap justify-content-between',
})`
  background-color: ${colors.lightGray};
  border-top: 1px solid ${colors.borderGray};
  margin: 0 -15px -15px;
  border-radius: 0.3rem;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

export const StyledModalHeaderDIV = styled.div.attrs({
  className: 'modal-header',
})`
  background-color: ${colors.lightGray};
  padding-bottom: 0;
  border-radius: 0.3rem;
  border-bottom: 1px solid ${colors.borderGray};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  .nav-tabs {
    border-bottom: 0;

    .nav-link {
      letter-spacing: -0.25pt;
      font-size: 80%;
      text-transform: uppercase;
      font-weight: bold;

      &:not(.active) {
        cursor: pointer;
      }
    }

    .nav-link:hover {
      background-color: #fff;
    }
  }
`;
